/**
 * Přiřazení uživatele / pozvánky k firmě tenantu.
 * `users.primary_company_id` má přednost. Když chybí, stačí jednoznačná shoda
 * legacy HR kódu (`protelco`) nebo obchodního jména s právě jednou firmou.
 */

export interface TenantCompanyMatchRef {
  id: string;
  legalName: string;
  code: string;
  /** `false` = nepoužívat pro shodu podle názvu/kódu. Chybějící hodnota = aktivní. */
  isActive?: boolean;
}

export interface TenantCompanyAssignmentHints {
  /** UUID firmy, nebo stejné pole s legacy kódem / názvem. */
  companyId?: string | null;
  /** Legacy HR kód (`protelco`) nebo `tenant_companies.code`. */
  companyCode?: string | null;
  /** Obchodní jméno (`ProTelco s.r.o.`). */
  companyName?: string | null;
}

const COMPANY_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function companyKey(value: string | null | undefined): string {
  return String(value ?? '').trim().toLowerCase();
}

/**
 * Vrátí `tenant_companies.id`, nebo `null` když uživatel firmu opravdu nemá
 * (žádné id a název/kód nesedí na právě jednu firmu).
 */
export function resolveTenantCompanyId(
  companies: readonly TenantCompanyMatchRef[],
  hints: TenantCompanyAssignmentHints,
): string | null {
  const catalog = companies.filter((company) => company.id.trim().length > 0);
  const explicit = String(hints.companyId ?? '').trim();
  if (explicit && catalog.some((company) => company.id === explicit)) return explicit;
  if (explicit && COMPANY_UUID_RE.test(explicit)) return explicit;

  const tokens = new Set(
    [explicit, hints.companyCode, hints.companyName].map(companyKey).filter((token) => token.length > 0),
  );
  if (tokens.size === 0) return null;

  const matched = new Map<string, TenantCompanyMatchRef>();
  for (const company of catalog) {
    if (company.isActive === false) continue;
    const code = companyKey(company.code);
    const legalName = companyKey(company.legalName);
    const hit = (code.length > 0 && tokens.has(code)) || (legalName.length > 0 && tokens.has(legalName));
    if (hit) matched.set(company.id, company);
  }
  if (matched.size === 1) return [...matched.keys()][0];
  return null;
}
